import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import SubpageHeader from "../components/SubpageHeader";
import ShowHero from "../components/ShowHero";
import ContentSection from "../components/ContentSection";
import ShowRecommendationCard from "../components/ShowRecommendationCard";
import SiteFooter from "../components/SiteFooter";
import Button from "../components/ui/Button";
import Typography from "../components/ui/Typography";
import TasteTag from "../components/ui/TasteTag";
import LoadingSpinner from "../components/LoadingSpinner";
import ErrorAlert from "../components/ErrorAlert";
import useWatchlist from "../hooks/useWatchlist";
import usePageMeta from "../hooks/usePageMeta";
import { fetchShowBySlug } from "../services/backendApi";
import { handleInternalLinkClick, navigateTo } from "../utils/navigation";
import {
  DEFAULT_OG_IMAGE,
  determineStatus,
  generateShowSlug,
  getPosterUrl,
  getShowField,
} from "./show/showUtils";
import { getContentRating, isNewSeries, normalizeSeasonNumber } from "../utils/showFields";
import { buildAggregateRatingSchema } from "../utils/showRatings";
import { buildShowSeoMeta } from "./show/showSeo";
import { formatMonthLabel } from "./month/monthSeo";
import { trackEvent } from "../utils/analytics";
import { resolveInitialPayload } from "../utils/initialPayload";

export const buildShowStructuredDataEntries = ({ show, slug, relatedShows = [] }) => {
  const title = getShowField(show, "title");
  const plot = getShowField(show, "plot");
  const releaseDate = getShowField(show, "releaseDate");
  const genres = getShowField(show, "genres", []);
  const aggregateRating = buildAggregateRatingSchema(show);
  const episodeCount = getShowField(show, "episodeCount");
  const posterUrl = getPosterUrl(show);
  const seasonNumber = getShowField(show, "seasonNumber");
  const originalTitle = getShowField(show, "originalTitle");
  const alternateTitles = getShowField(show, "alternateTitles", []);
  const trailers = getShowField(show, "trailers", []);
  const contentRatingValue = getContentRating(show);
  const alternateNames = [originalTitle, ...alternateTitles].filter(Boolean).slice(0, 5);

  const validRelatedShows = Array.isArray(relatedShows)
    ? relatedShows
        .filter((related) => typeof related?.slug === "string" && related.slug.trim() && typeof related?.title === "string" && related.title.trim())
        .slice(0, 3)
    : [];
  const relatedShowRefs = validRelatedShows.map((related) => ({
    "@type": "TVSeries",
    name: related.title.trim(),
    url: `https://premierelens.com/show/${related.slug.trim()}`,
  }));

  const seriesJson = {
    "@context": "https://schema.org",
    "@type": "TVSeries",
    name: title,
    ...(alternateNames.length > 0
      ? { alternateName: alternateNames.length === 1 ? alternateNames[0] : alternateNames }
      : {}),
    url: `https://premierelens.com/show/${slug}`,
    ...(releaseDate ? { datePublished: releaseDate } : {}),
    ...(plot ? { description: plot } : {}),
    ...(genres.length ? { genre: genres.slice(0, 5) } : {}),
    ...(aggregateRating ? { aggregateRating } : {}),
    ...(episodeCount ? { numberOfEpisodes: Number(episodeCount) } : {}),
    ...(seasonNumber ? { numberOfSeasons: Number(seasonNumber) } : {}),
    ...(releaseDate ? { startDate: releaseDate } : {}),
    ...(contentRatingValue ? { contentRating: contentRatingValue } : {}),
    ...(posterUrl ? { image: posterUrl } : {}),
    ...(relatedShowRefs.length > 0 ? { isRelatedTo: relatedShowRefs } : {}),
  };

  const relatedItemList = validRelatedShows.length > 0
    ? {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: `If you like ${title}, watch these`,
        itemListOrder: "https://schema.org/ItemListOrderAscending",
        numberOfItems: validRelatedShows.length,
        itemListElement: validRelatedShows.map((related, index) => {
          const relatedUrl = `https://premierelens.com/show/${related.slug.trim()}`;
          const bridgeText = typeof related.bridge === "string" && related.bridge.trim()
            ? related.bridge.trim()
            : null;
          const relatedPlot = typeof related.plotOverview === "string" && related.plotOverview.trim()
            ? related.plotOverview.trim()
            : typeof related.summary === "string" && related.summary.trim()
              ? related.summary.trim()
              : typeof related.omdbData?.plot === "string" && related.omdbData.plot.trim()
                ? related.omdbData.plot.trim()
                : null;
          const relatedPoster = related.posterMetadata?.thumbnail || related.poster || related.omdbData?.poster || null;
          const item = {
            "@type": "TVSeries",
            name: related.title.trim(),
            url: relatedUrl,
            ...(bridgeText
              ? { description: bridgeText }
              : relatedPlot
                ? { description: relatedPlot }
                : {}),
            ...(relatedPoster ? { image: relatedPoster } : {}),
          };
          return {
            "@type": "ListItem",
            position: index + 1,
            url: relatedUrl,
            item,
          };
        }),
      }
    : null;

  const releaseYearMonth = typeof releaseDate === "string" && /^\d{4}-\d{2}-\d{2}$/.test(releaseDate)
    ? releaseDate.slice(0, 7)
    : null;
  const breadcrumbItems = [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://premierelens.com" },
  ];
  if (releaseYearMonth) {
    breadcrumbItems.push({
      "@type": "ListItem",
      position: 2,
      name: `${formatMonthLabel(releaseYearMonth)} Premieres`,
      item: `https://premierelens.com/month/${releaseYearMonth}`,
    });
  }
  breadcrumbItems.push({
    "@type": "ListItem",
    position: breadcrumbItems.length + 1,
    name: title,
    item: `https://premierelens.com/show/${slug}`,
  });

  const breadcrumbs = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbItems,
  };

  const faqEntries = [];
  if (typeof episodeCount === "number" && episodeCount > 0) {
    faqEntries.push({
      "@type": "Question",
      name: `How many episodes are in ${title}?`,
      acceptedAnswer: {
        "@type": "Answer",
        text: `${title} has ${episodeCount} episodes in this listed season.`,
      },
    });
  }
  if (releaseDate) {
    faqEntries.push({
      "@type": "Question",
      name: `When does ${title} premiere?`,
      acceptedAnswer: {
        "@type": "Answer",
        text: `${title} premieres on ${releaseDate}.`,
      },
    });
  }
  if (typeof seasonNumber === "number" && seasonNumber > 0) {
    faqEntries.push({
      "@type": "Question",
      name: `Is ${title} a new series?`,
      acceptedAnswer: {
        "@type": "Answer",
        text:
          isNewSeries(show)
            ? `${title} is a new series (Season 1).`
            : `${title} is a returning series (Season ${seasonNumber}${show.seasonPart ? ` · ${show.seasonPart}` : ""}).`,
      },
    });
  }

  const faqJson = faqEntries.length > 0
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqEntries,
      }
    : null;

  const validTrailerEntries = Array.isArray(trailers)
    ? trailers
        .map((trailer, index) => {
          const url =
            typeof trailer === "string"
              ? trailer
              : typeof trailer?.url === "string"
                ? trailer.url
                : null;
          if (!url) return null;
          const sourceName = typeof trailer?.name === "string" ? trailer.name.trim() : "";
          const genericTrailerName = seasonNumber
            ? `${title} Season ${seasonNumber} Trailer`
            : `${title} Trailer`;
          const name =
            sourceName && !/^trailer$/i.test(sourceName)
              ? sourceName
              : trailers.length > 1 || index > 0
                ? `${genericTrailerName} ${index + 1}`
                : genericTrailerName;
          const item = {
            "@context": "https://schema.org",
            "@type": "VideoObject",
            name,
            description: plot || `${title} trailer`,
            contentUrl: url,
            ...(posterUrl ? { thumbnailUrl: [posterUrl] } : {}),
            ...(releaseDate ? { uploadDate: releaseDate } : {}),
          };
          return item;
        })
        .filter(Boolean)
    : [];

  const schemaEntries = [
    seriesJson,
    breadcrumbs,
    ...(faqJson ? [faqJson] : []),
    ...(relatedItemList ? [relatedItemList] : []),
    ...validTrailerEntries,
  ];

  return schemaEntries;
};

const ShowStructuredData = ({ show, slug, relatedShows = [] }) => {
  const schemaEntries = buildShowStructuredDataEntries({ show, slug, relatedShows });

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaEntries) }}
    />
  );
};

ShowStructuredData.propTypes = {
  show: PropTypes.object.isRequired,
  slug: PropTypes.string.isRequired,
  relatedShows: PropTypes.arrayOf(PropTypes.shape({
    slug: PropTypes.string,
    title: PropTypes.string,
    bridge: PropTypes.string,
    poster: PropTypes.string,
    plotOverview: PropTypes.string,
    summary: PropTypes.string,
  })),
};

const splitEditorialAppeal = (value = "") => {
  const text = String(value).replace(/\s+/g, " ").trim();
  if (!text) return [];
  const sentences = text.match(/[^.!?]+[.!?]+(?:\s|$)/g);
  if (!sentences || sentences.length < 2) {
    return [text];
  }

  const first = sentences[0].trim();
  const rest = text.slice(first.length).trim();
  return [first, rest].filter(Boolean);
};

const selectDisplayTasteTags = (profile, limit = 4) => {
  const tags = Array.isArray(profile?.tags) ? profile.tags : [];
  return tags
    .filter((tag) => typeof tag === "string" && tag.trim())
    .map((tag) => tag.trim())
    .slice(0, limit);
};

const selectEditorialBriefParagraphs = (brief) => {
  const body = Array.isArray(brief?.body) ? brief.body : [];
  return body
    .filter((paragraph) => typeof paragraph === "string" && paragraph.trim())
    .map((paragraph) => paragraph.replace(/\s+/g, " ").trim())
    .slice(0, 2);
};

const ShowPage = ({ slug, initialShowPayload = null }) => {
  const initialPayload = useMemo(
    () => resolveInitialPayload(initialShowPayload, (payload) => generateShowSlug(payload.data) === slug),
    [initialShowPayload, slug]
  );
  const [show, setShow] = useState(() => initialPayload?.data || null);
  const [isArchived, setIsArchived] = useState(() => Boolean(initialPayload?.isArchived));
  const [loading, setLoading] = useState(() => !initialPayload?.data);
  const [error, setError] = useState("");
  const [notFound, setNotFound] = useState(false);
  const [relatedShows, setRelatedShows] = useState(() =>
    Array.isArray(initialPayload?.relatedShows) ? initialPayload.relatedShows : []
  );
  const [curationReasons, setCurationReasons] = useState(() =>
    Array.isArray(initialPayload?.curationReasons) ? initialPayload.curationReasons : []
  );
  const [hasShowsLikePage, setHasShowsLikePage] = useState(() => Boolean(initialPayload?.hasShowsLikePage));
  const [showsLikePickCount, setShowsLikePickCount] = useState(() =>
    typeof initialPayload?.showsLikePickCount === "number" ? initialPayload.showsLikePickCount : 0
  );
  const [bestOfPlacement, setBestOfPlacement] = useState(() => initialPayload?.bestOfPlacement || null);
  const { isInWatchlist, toggleWatchlist, upsertWatchlistEntries } = useWatchlist();
  const hasBridgedRelatedShows = relatedShows.length > 0
    && relatedShows.every((related) => typeof related?.bridge === "string" && related.bridge.trim());
  const additionalShowsLikeCount = Math.max(0, showsLikePickCount - relatedShows.slice(0, 3).length);

  useEffect(() => {
    if (initialPayload?.data) {
      return;
    }
    let active = true;
    const run = async () => {
      try {
        setLoading(true);
        const payload = await fetchShowBySlug(slug);
        if (!active) return;
        if (payload?.redirectTo) {
          window.location.replace(payload.redirectTo);
          return;
        }
        if (payload?.notFound || !payload?.data) {
          setNotFound(true);
          setShow(null);
          return;
        }
        setShow(payload.data);
        setIsArchived(Boolean(payload.isArchived));
        setRelatedShows(Array.isArray(payload.relatedShows) ? payload.relatedShows : []);
        setCurationReasons(Array.isArray(payload.curationReasons) ? payload.curationReasons : []);
        setHasShowsLikePage(Boolean(payload.hasShowsLikePage));
        setShowsLikePickCount(typeof payload.showsLikePickCount === "number" ? payload.showsLikePickCount : 0);
        setBestOfPlacement(payload.bestOfPlacement || null);
        setNotFound(false);
        setError("");
      } catch (nextError) {
        if (!active) return;
        setError(nextError.message || "Failed to load show");
      } finally {
        if (active) setLoading(false);
      }
    };
    run();
    return () => { active = false; };
  }, [initialPayload, slug]);

  const fields = useMemo(() => {
    const title = getShowField(show, "title");
    const year = getShowField(show, "year");
    // A checked premise from the show's fact sheet, on pages in the facts rollout.
    const premise = typeof show?.premise === "string" ? show.premise.trim() : "";
    const plot = premise || getShowField(show, "plot");
    const rating = getShowField(show, "rating");
    const releaseDate = getShowField(show, "releaseDate");
    const genres = getShowField(show, "genres", []);
    const posterUrl = getPosterUrl(show);
    const status = determineStatus(show);
    const seasonNumber = getShowField(show, "seasonNumber");
    const episodeCount = getShowField(show, "episodeCount");
    const semanticAppeal = typeof show?.semanticAppeal === "string" ? show.semanticAppeal.trim() : "";
    const displayTasteTags = selectDisplayTasteTags(show?.displayTasteProfile, 4);
    const editorialBriefParagraphs = selectEditorialBriefParagraphs(show?.editorialBrief);
    const editorialParagraphs = editorialBriefParagraphs.length > 0
      ? editorialBriefParagraphs
      : splitEditorialAppeal(semanticAppeal);
    return {
      title,
      year,
      plot,
      hasPremise: Boolean(premise),
      rating,
      releaseDate,
      genres,
      posterUrl,
      status,
      seasonNumber,
      episodeCount,
      semanticAppeal,
      editorialParagraphs,
      displayTasteTags,
    };
  }, [show]);

  const showSeoMeta = show ? buildShowSeoMeta({ show, slug, curationReasons, relatedShows }) : null;
  const metaTitle = showSeoMeta?.title || "Show Details | PremiereLens";
  const metaDescription = showSeoMeta?.description || "View TV show details on PremiereLens.";
  const ogImage = show ? fields.posterUrl || DEFAULT_OG_IMAGE : DEFAULT_OG_IMAGE;

  usePageMeta({
    title: metaTitle,
    description: metaDescription,
    pathname: `/show/${slug}`,
    image: ogImage,
    noindex: notFound,
    ogType: "video.tv_show",
  });

  useEffect(() => {
    if (show) {
      upsertWatchlistEntries([show]);
    }
  }, [show, upsertWatchlistEntries]);

  useEffect(() => {
    if (!show) return undefined;
    const thresholds = [50, 90];
    const seen = new Set();
    const handleScroll = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      const percent = Math.round((window.scrollY / scrollable) * 100);
      thresholds.forEach((threshold) => {
        if (percent >= threshold && !seen.has(threshold)) {
          seen.add(threshold);
          trackEvent("show_scroll_depth", {
            slug,
            depth_percent: threshold,
          });
        }
      });
      if (seen.size === thresholds.length) {
        window.removeEventListener("scroll", handleScroll);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [show, slug]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <SubpageHeader backHref={isArchived ? "/archive" : "/"} backLabel={isArchived ? "Archive" : "Home"} />
        <main id="main-content" tabIndex={-1} className="route-shell route-shell--standard">
          <div className="flex justify-center py-16">
            <LoadingSpinner />
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <SubpageHeader backHref={isArchived ? "/archive" : "/"} backLabel={isArchived ? "Archive" : "Home"} />
        <main id="main-content" tabIndex={-1} className="route-shell route-shell--standard">
          <div className="max-w-2xl mx-auto">
            <ErrorAlert message={error} />
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  // Not found state
  if (notFound || !show) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <SubpageHeader backHref={isArchived ? "/archive" : "/"} backLabel={isArchived ? "Archive" : "Home"} />
        <main id="main-content" tabIndex={-1} className="route-shell route-shell--standard">
          <div className="max-w-2xl mx-auto text-center py-16">
            <Typography variant="h2" className="text-foreground mb-4">
              Show Not Found
            </Typography>
            <Typography variant="body" className="text-muted-foreground mb-6">
              We couldn&apos;t find a show for this URL. It may have moved or been removed.
            </Typography>
            <Button variant="default" onClick={() => navigateTo("/")}>
              Back to Home
            </Button>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SubpageHeader backHref={isArchived ? "/archive" : "/"} backLabel={isArchived ? "Archive" : "Home"} />
      <ShowStructuredData show={show} slug={slug} relatedShows={relatedShows} />

      <main id="main-content" tabIndex={-1} className="route-shell route-shell--media">
        <div className="route-shell__media-inner space-y-12">
          {/* Hero Section */}
          <ShowHero
            show={show}
            isArchived={isArchived}
            isInWatchlist={isInWatchlist}
            onToggleWatchlist={toggleWatchlist}
            editorialSlot={fields.editorialParagraphs.length > 0 ? (
              <section className="show-editorial-appeal show-editorial-appeal--hero" aria-labelledby="show-editorial-appeal-title">
                <Typography
                  id="show-editorial-appeal-title"
                  as="h2"
                  variant="h3"
                  className="text-foreground sp-heading-rule pb-3 border-b border-dashed border-accent-gold/60"
                >
                  What to Expect
                </Typography>
                <div className={`show-editorial-appeal__body${fields.displayTasteTags.length === 0 ? " show-editorial-appeal__body--solo" : ""}`}>
                  <div className="show-editorial-appeal__copy">
                    {fields.editorialParagraphs.map((paragraph) => (
                      <Typography key={paragraph} variant="body" className="show-editorial-appeal__paragraph">
                        {paragraph}
                      </Typography>
                    ))}
                  </div>
                  {fields.displayTasteTags.length > 0 && (
                    <aside className="show-editorial-appeal__signals" aria-label={`${fields.title} viewing details`}>
                      <Typography variant="caption" className="show-editorial-appeal__signals-label text-label">
                        What it’s like
                      </Typography>
                      <div className="show-editorial-appeal__tags">
                        {fields.displayTasteTags.map((tag) => (
                          <TasteTag key={tag}>
                            {tag}
                          </TasteTag>
                        ))}
                      </div>
                    </aside>
                  )}
                </div>
              </section>
            ) : null}
          />

          {fields.plot && (
            <ContentSection
              title={fields.hasPremise ? "Premise" : normalizeSeasonNumber(fields.seasonNumber) > 1 ? "Series premise" : "Official premise"}
              className="show-official-premise"
            >
              <Typography variant="body" className="show-official-premise__copy">
                {fields.plot}
              </Typography>
            </ContentSection>
          )}

          {/* Related Shows */}
          {relatedShows.length > 0 && (
            <ContentSection title="What to watch next">
              <Typography variant="body-small" className="max-w-[70ch] text-muted-foreground">
                {hasBridgedRelatedShows
                  ? "Start with the closest match, then check out two more shows with a similar feel, pace, mood, or theme."
                  : "More shows that sit near this one in tone, audience, or subject matter."}
              </Typography>
              <div className="show-recommendations-layout">
                {relatedShows.slice(0, 3).map((related, index) => {
                  const relatedSlug = generateShowSlug(related);
                  return (
                    <ShowRecommendationCard
                      key={relatedSlug}
                      related={related}
                      priority={index === 0}
                      featured={index === 0}
                      anchorTitle={show.title}
                    />
                  );
                })}
              </div>
              <div className="flex flex-wrap items-center gap-3 border-t border-dashed border-accent-gold/60 pt-4">
                <Typography variant="body-small" className="text-muted-foreground">
                  More recommendations
                </Typography>
                <Button variant="outline" size="sm" asChild>
                  <a
                    href="/most-recommended"
                    onClick={(event) => handleInternalLinkClick(event, "/most-recommended")}
                  >
                    Start from a different show
                  </a>
                </Button>
                {hasShowsLikePage && additionalShowsLikeCount > 0 && (
                  <Button variant="outline" size="sm" asChild>
                    <a
                      href={`/shows-like/${slug}`}
                      onClick={(event) => handleInternalLinkClick(event, `/shows-like/${slug}`)}
                    >
                      See {additionalShowsLikeCount} more {additionalShowsLikeCount === 1 ? "show" : "shows"} like {fields.title}
                    </a>
                  </Button>
                )}
              </div>
            </ContentSection>
          )}

          {/* Explore More */}
          <ContentSection title="Explore more">
            <div className="flex flex-wrap gap-3">
              {fields.releaseDate && /^\d{4}-\d{2}/.test(fields.releaseDate) && (
                <Button variant="default" asChild>
                  <a
                    href={`/month/${fields.releaseDate.slice(0, 7)}`}
                    onClick={(event) => handleInternalLinkClick(event, `/month/${fields.releaseDate.slice(0, 7)}`)}
                  >
                    All {formatMonthLabel(fields.releaseDate.slice(0, 7))} Premieres
                  </a>
                </Button>
              )}
              {bestOfPlacement && (
                <Button variant="outline" asChild>
                  <a
                    href={`/best-of/${bestOfPlacement.yearMonth}`}
                    onClick={(event) => handleInternalLinkClick(event, `/best-of/${bestOfPlacement.yearMonth}`)}
                  >
                    #{bestOfPlacement.rank} in Best of {formatMonthLabel(bestOfPlacement.yearMonth)}
                  </a>
                </Button>
              )}
              {!isArchived && (
                <Button variant="outline" asChild>
                  <a href="/" onClick={(event) => handleInternalLinkClick(event, "/")}>
                    Back to upcoming
                  </a>
                </Button>
              )}
              <Button variant="outline" asChild>
                <a href="/archive" onClick={(event) => handleInternalLinkClick(event, "/archive")}>
                  Browse Archive
                </a>
              </Button>
            </div>
          </ContentSection>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
};

ShowPage.propTypes = {
  slug: PropTypes.string.isRequired,
  initialShowPayload: PropTypes.shape({
    data: PropTypes.object,
    isArchived: PropTypes.bool,
    relatedShows: PropTypes.arrayOf(PropTypes.shape({
      slug: PropTypes.string,
      bridge: PropTypes.string,
    })),
    hasShowsLikePage: PropTypes.bool,
    showsLikePickCount: PropTypes.number,
  }),
};

export default ShowPage;
